import { Button, Checkbox, Divider, Input, InputNumber, Radio, Select } from "antd/es";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { DropdownChevronIcon } from "../common/dropdown-chevron-icon";
import { KDatePicker } from "../date/date";
import { BooleanFilterTypes, DateFilterTypes, NumberFilterTypes, StringFilterTypes, ValueType } from "./base-table";
import {
  BLANK_FILTER_KEY,
  CellBgColorFn,
  getUniqueCellBgColors,
  getUniqueColumnValues,
  parseColorToRgb,
  UniqueFilterValue
} from "./table-filter-helpers";
import TriStateSwitch from "./tri-state-switch";

type FilterTab = "condition" | "value" | "color";

const { Option } = Select;

declare let window: any;

interface CustomFilterDropdownProps {
  selectedKeys: any;
  setSelectedKeys: any;
  confirm: any;
  clearFilters: any;
  dataType: any;
  visible: any;
  initialFilters: any;
  columnId: any;
  tableStyle?: any;
  dateSearchFormat?: string;
  dataSource?: unknown[];
  dataValue?: string;
  calculatedCellBgColor?: CellBgColorFn;
}

const CustomFilterDropdown = ({
  selectedKeys,
  setSelectedKeys,
  confirm,
  clearFilters,
  dataType,
  visible,
  initialFilters,
  columnId,
  tableStyle,
  dateSearchFormat,
  dataSource,
  dataValue,
  calculatedCellBgColor
}: CustomFilterDropdownProps) => {
  const [searchType, setSearchType] = useState<
    StringFilterTypes | NumberFilterTypes | DateFilterTypes | BooleanFilterTypes
  >(
    dataType === ValueType.String
      ? StringFilterTypes.Contains
      : ValueType.Number
        ? NumberFilterTypes.Equals
        : DateFilterTypes.Equals
  );
  const [searchValue, setSearchValue] = useState<string>(selectedKeys?.value);
  const [searchValue2, setSearchValue2] = useState<string>(selectedKeys?.value2);
  const [logicalOperator, setLogicalOperator] = useState<"AND" | "OR">(selectedKeys?.logicalOperator || "AND");

  const [secondSearchType, setSecondSearchType] = useState<
    StringFilterTypes | NumberFilterTypes | DateFilterTypes | BooleanFilterTypes
  >(selectedKeys?.secondType);
  const [secondSearchValue, setSecondSearchValue] = useState<string>(selectedKeys?.secondValue || "");
  const [secondSearchValue2, setSecondSearchValue2] = useState<string>(selectedKeys?.secondValue2 || "");
  const [showSecondFilter, setShowSecondFilter] = useState<boolean>(
    Boolean(selectedKeys?.secondValue || selectedKeys?.secondType)
  );

  const language = localStorage.getItem("ml");
  const isTr = language === "tr_TR";

  const hasColorFilter: boolean = typeof calculatedCellBgColor === "function";

  const [activeTab, setActiveTab] = useState<FilterTab>((selectedKeys?.filterMode as FilterTab) || "condition");
  const [valueSearch, setValueSearch] = useState<string>("");
  const [selectedValues, setSelectedValues] = useState<Set<string>>(
    new Set<string>(selectedKeys?.selectedValues || [])
  );
  const [selectedColors, setSelectedColors] = useState<Set<string>>(
    new Set<string>(selectedKeys?.selectedColors || [])
  );

  const uniqueValues: UniqueFilterValue[] = useMemo(() => {
    // Match is on the raw value (decoupled from label); only the displayed label is
    // formatted. Dates would otherwise show a raw ISO string, so format them with the
    // column's own dateSearchFormat (the same format the date-condition picker uses).
    const formatValueLabel = (raw: unknown): string => {
      if (dataType === ValueType.Date && raw !== null && raw !== undefined && raw !== "") {
        const parsed = dateSearchFormat
          ? moment(raw as moment.MomentInput, dateSearchFormat)
          : moment(raw as moment.MomentInput);
        if (parsed.isValid()) {
          return parsed.format(dateSearchFormat || "YYYY-MM-DD");
        }
      }
      return String(raw);
    };
    return getUniqueColumnValues(dataSource, dataValue || "", language, formatValueLabel);
  }, [dataSource, dataValue, language, dataType, dateSearchFormat]);

  const visibleValues: UniqueFilterValue[] = useMemo(() => {
    const term: string = valueSearch.trim().toLowerCase();
    if (!term) {
      return uniqueValues;
    }
    return uniqueValues.filter((item: UniqueFilterValue): boolean => item.label.toLowerCase().includes(term));
  }, [uniqueValues, valueSearch]);

  const uniqueColors: string[] = useMemo(
    () => getUniqueCellBgColors(dataSource, calculatedCellBgColor),
    [dataSource, calculatedCellBgColor]
  );

  const isString = (value: StringFilterTypes | NumberFilterTypes | DateFilterTypes | BooleanFilterTypes) => {
    if (
      value === StringFilterTypes.Contains ||
      value === StringFilterTypes.DoesNotContain ||
      value === StringFilterTypes.Equals ||
      value === StringFilterTypes.DoesNotEqual ||
      value === StringFilterTypes.BeginsWith ||
      value === StringFilterTypes.EndsWith ||
      value === StringFilterTypes.Blank ||
      value === StringFilterTypes.NotBlank
    ) {
      return true;
    }
    return false;
  };

  const isNumber = (value: NumberFilterTypes | StringFilterTypes | DateFilterTypes | BooleanFilterTypes) => {
    if (
      value === NumberFilterTypes.Equals ||
      value === NumberFilterTypes.DoesNotEqual ||
      value === NumberFilterTypes.GreaterThan ||
      value === NumberFilterTypes.GreaterThanOrEqual ||
      value === NumberFilterTypes.LessThan ||
      value === NumberFilterTypes.LessThanOrEqual ||
      value === NumberFilterTypes.Blank ||
      value === NumberFilterTypes.NotBlank ||
      value === NumberFilterTypes.Between
    ) {
      return true;
    }
    return false;
  };

  const isDate = (value: DateFilterTypes | StringFilterTypes | NumberFilterTypes | BooleanFilterTypes) => {
    if (
      value === DateFilterTypes.After ||
      value === DateFilterTypes.Before ||
      value === DateFilterTypes.Between ||
      value === DateFilterTypes.Equals ||
      value === DateFilterTypes.DoesNotEqual ||
      value === DateFilterTypes.Blank ||
      value === DateFilterTypes.NotBlank
    ) {
      return true;
    }
    return false;
  };

  const isBoolean = (value: StringFilterTypes | NumberFilterTypes | DateFilterTypes | BooleanFilterTypes) => {
    if (
      value === BooleanFilterTypes.Equals ||
      value === BooleanFilterTypes.DoesNotEqual ||
      value === BooleanFilterTypes.Blank ||
      value === BooleanFilterTypes.NotBlank
    ) {
      return true;
    }
    if (dataType === ValueType.Boolean) {
      return true;
    }
    return false;
  };

  const isBlankType = (value: StringFilterTypes | NumberFilterTypes | DateFilterTypes | BooleanFilterTypes) => {
    return (
      value === StringFilterTypes.Blank ||
      value === NumberFilterTypes.Blank ||
      value === DateFilterTypes.Blank ||
      value === BooleanFilterTypes.Blank ||
      value === StringFilterTypes.NotBlank ||
      value === NumberFilterTypes.NotBlank ||
      value === DateFilterTypes.NotBlank ||
      value === BooleanFilterTypes.NotBlank
    );
  };

  useEffect(() => {
    if (searchValue && searchValue.toString().trim() !== "") {
      setShowSecondFilter(true);
    }
  }, [searchValue]);

  useEffect(() => {
    if (isBlankType(searchType)) {
      setShowSecondFilter(true);
    }
  }, [searchType]);

  useEffect(() => {
    if (visible) {
      // Value/color filters carry their own shape (selectedValues/selectedColors); don't
      // stamp condition fields (type/secondType/…) onto them when the dropdown re-opens.
      if (selectedKeys?.filterMode === "value" || selectedKeys?.filterMode === "color") {
        return;
      }
      const searchType = findDataType(dataType);
      const currentType = selectedKeys?.type ?? searchType;
      setSearchType(currentType);
      setSelectedKeys({
        ...selectedKeys,
        type: currentType,
        columnId,
        logicalOperator: selectedKeys?.logicalOperator || logicalOperator,
        secondType: selectedKeys?.secondType,
        secondValue: selectedKeys?.secondValue,
        secondValue2: selectedKeys?.secondValue2
      });

      if (selectedKeys?.secondValue || selectedKeys?.secondType) {
        setShowSecondFilter(true);
        setSecondSearchType(selectedKeys?.secondType || searchType);
        setSecondSearchValue(selectedKeys?.secondValue || "");
        setSecondSearchValue2(selectedKeys?.secondValue2 || "");
      }
    }
  }, [visible]);

  useEffect(() => {
    if (initialFilters && initialFilters.value && initialFilters.type && initialFilters.value !== searchValue) {
      const searchType = findDataType(dataType);
      setSearchType(searchType);
      setSelectedKeys({
        ...selectedKeys,
        value: initialFilters.value,
        type: initialFilters.type,
        columnId,
        logicalOperator: initialFilters.logicalOperator || logicalOperator,
        secondType: initialFilters.secondType,
        secondValue: initialFilters.secondValue,
        secondValue2: initialFilters.secondValue2
      });
      setSearchValue(initialFilters.value);

      if (initialFilters.secondValue || initialFilters.secondType) {
        setShowSecondFilter(true);
        setSecondSearchType(initialFilters.secondType || searchType);
        setSecondSearchValue(initialFilters.secondValue || "");
        setSecondSearchValue2(initialFilters.secondValue2 || "");
      }

      if (initialFilters.logicalOperator) {
        setLogicalOperator(initialFilters.logicalOperator);
      }
      confirm();
    }
  }, []);

  const findDataType = (value: string) => {
    if (value === ValueType.Boolean) {
      return BooleanFilterTypes.Equals;
    }
    if (value === ValueType.String) {
      return StringFilterTypes.Contains;
    }
    if (value === ValueType.Number) {
      return NumberFilterTypes.Equals;
    }
    if (value === ValueType.Date) {
      return DateFilterTypes.Equals;
    }
    return StringFilterTypes.Contains;
  };

  const handleSearch = () => {
    if (activeTab === "value") {
      setSelectedKeys({
        filterMode: "value",
        columnId,
        logicalOperator: "AND",
        selectedValues: Array.from(selectedValues)
      });
      confirm();
      return;
    }

    if (activeTab === "color") {
      setSelectedKeys({
        filterMode: "color",
        colorTarget: "background",
        columnId,
        logicalOperator: "AND",
        selectedColors: Array.from(selectedColors)
      });
      confirm();
      return;
    }

    const fullFilter = {
      filterMode: "condition",
      value: searchValue,
      value2: searchValue2,
      type: searchType,
      columnId,
      logicalOperator,
      secondType: secondSearchType,
      secondValue: secondSearchValue,
      secondValue2: secondSearchValue2
    };

    setSelectedKeys(fullFilter);
    confirm();
  };

  const handleReset = () => {
    setSelectedKeys("");
    const searchType = findDataType(dataType);
    setSearchType(searchType);
    setSecondSearchType(searchType);
    setSearchValue("");
    setSecondSearchValue("");
    setSecondSearchValue2("");
    setShowSecondFilter(false);
    setSelectedValues(new Set<string>());
    setSelectedColors(new Set<string>());
    setValueSearch("");
    clearFilters();
    confirm();
  };

  const toggleSelectedValue = (key: string, checked: boolean) => {
    setSelectedValues((prev: Set<string>): Set<string> => {
      const next: Set<string> = new Set<string>(prev);
      if (checked) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  };

  const toggleSelectedColor = (color: string, checked: boolean) => {
    setSelectedColors((prev: Set<string>): Set<string> => {
      const next: Set<string> = new Set<string>(prev);
      if (checked) {
        next.add(color);
      } else {
        next.delete(color);
      }
      return next;
    });
  };

  const selectAllVisibleValues = () => {
    setSelectedValues((prev: Set<string>): Set<string> => {
      const next: Set<string> = new Set<string>(prev);
      visibleValues.forEach((item: UniqueFilterValue): void => {
        next.add(item.key);
      });
      return next;
    });
  };

  const clearAllValues = () => {
    setSelectedValues(new Set<string>());
  };

  const handleSearchValueChange = (e, secondValue?: boolean) => {
    if (secondValue) {
      const value2 = e && e.target === undefined ? e : e.target ? e.target.value : e;
      setSearchValue2(value2);
      setSelectedKeys({
        value: searchValue,
        value2,
        type: searchType,
        columnId,
        logicalOperator,
        secondType: secondSearchType,
        secondValue: secondSearchValue,
        secondValue2: secondSearchValue2
      });
      return;
    }

    const value = e && e.target === undefined ? e : e.target ? e.target.value : e;
    setSearchValue(value);

    if (value && value.toString().trim() !== "") {
      setShowSecondFilter(true);
    }

    setSelectedKeys({
      value,
      value2: searchValue2,
      type: searchType,
      columnId,
      logicalOperator,
      secondType: secondSearchType,
      secondValue: secondSearchValue,
      secondValue2: secondSearchValue2
    });
  };

  const handleSecondSearchValueChange = (e, secondValue?: boolean) => {
    if (secondValue) {
      const value2 = e && e.target === undefined ? e : e.target ? e.target.value : e;
      setSecondSearchValue2(value2);
      setSelectedKeys({
        value: searchValue,
        value2: searchValue2,
        type: searchType,
        columnId,
        logicalOperator,
        secondType: secondSearchType,
        secondValue: secondSearchValue,
        secondValue2: value2
      });
      return;
    }

    const value = e && e.target === undefined ? e : e.target ? e.target.value : e;
    setSecondSearchValue(value);
    setSelectedKeys({
      value: searchValue,
      value2: searchValue2,
      type: searchType,
      columnId,
      logicalOperator,
      secondType: secondSearchType,
      secondValue: value,
      secondValue2: secondSearchValue2
    });
  };

  const handleLogicalOperatorChange = (e) => {
    const newOperator = e.target.value;
    setLogicalOperator(newOperator);
    setSelectedKeys({
      ...selectedKeys,
      value: searchValue,
      value2: searchValue2,
      type: searchType,
      columnId,
      logicalOperator: newOperator,
      secondType: secondSearchType,
      secondValue: secondSearchValue,
      secondValue2: secondSearchValue2
    });
  };

  const isBlank =
    searchType === StringFilterTypes.Blank ||
    searchType === NumberFilterTypes.Blank ||
    searchType === DateFilterTypes.Blank ||
    searchType === BooleanFilterTypes.Blank;
  const isNotBlank =
    searchType === StringFilterTypes.NotBlank ||
    searchType === NumberFilterTypes.NotBlank ||
    searchType === DateFilterTypes.NotBlank ||
    searchType === BooleanFilterTypes.NotBlank;

  const isSecondBlank =
    secondSearchType === StringFilterTypes.Blank ||
    secondSearchType === NumberFilterTypes.Blank ||
    secondSearchType === DateFilterTypes.Blank ||
    secondSearchType === BooleanFilterTypes.Blank;
  const isSecondNotBlank =
    secondSearchType === StringFilterTypes.NotBlank ||
    secondSearchType === NumberFilterTypes.NotBlank ||
    secondSearchType === DateFilterTypes.NotBlank ||
    secondSearchType === BooleanFilterTypes.NotBlank;

  const isStringDataType = dataType === ValueType.String || !dataType;
  const isDesignTime = window.kuika.isDesignTime;

  const getContrastColor = (bgColor: string): string => {
    const rgb = parseColorToRgb(bgColor);
    if (!rgb) return "#000000";
    const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
    return luminance > 0.5 ? "#000000" : "#ffffff";
  };

  const tableBgColor = tableStyle?.backgroundColor || tableStyle?.background;
  const hasTableBgColor = tableBgColor && tableBgColor !== "transparent";
  const contrastColor = hasTableBgColor ? getContrastColor(tableBgColor) : "#000000";

  const uniqueWrapperClass = `kuika-filter-dropdown-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const surfaceColor: string = hasTableBgColor ? tableBgColor : "#ffffff";

  const renderTabIcon = (tab: FilterTab): JSX.Element => {
    const svgProps = {
      width: 13,
      height: 13,
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: 2,
      strokeLinecap: "round" as const,
      strokeLinejoin: "round" as const,
      style: { flexShrink: 0 }
    };
    if (tab === "condition") {
      return (
        <svg {...svgProps}>
          <path d="M3 5h18M6 12h12M10 19h4" />
        </svg>
      );
    }
    if (tab === "value") {
      return (
        <svg {...svgProps}>
          <path d="M9 6h12M9 12h12M9 18h12M4 6h.01M4 12h.01M4 18h.01" />
        </svg>
      );
    }
    return (
      <svg {...svgProps}>
        <path d="M12 3s6 6.3 6 10a6 6 0 0 1-12 0c0-3.7 6-10 6-10z" />
      </svg>
    );
  };

  const renderTabButton = (tab: FilterTab, label: string) => {
    const active: boolean = activeTab === tab;
    return (
      <button
        type="button"
        data-testid={`filter-tab-${tab}`}
        className={`kfd-seg${active ? " kfd-seg--active" : ""}`}
        onClick={() => setActiveTab(tab)}
        style={{
          flex: 1,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 5,
          padding: "5px 8px",
          cursor: "pointer",
          border: "none",
          borderRadius: 6,
          fontSize: 12,
          letterSpacing: 0.2,
          background: active ? contrastColor : "transparent",
          color: active ? surfaceColor : contrastColor,
          fontWeight: active ? 600 : 500,
          opacity: active ? 1 : 0.62,
          boxShadow: active ? `0 1px 2px ${contrastColor}33` : "none"
        }}
      >
        {renderTabIcon(tab)}
        {label}
      </button>
    );
  };

  return (
    <div
      className={uniqueWrapperClass}
      style={{
        padding: 10,
        minWidth: 232,
        backgroundColor: surfaceColor,
        borderRadius: 8,
        fontFamily: "inherit"
      }}
    >
      <style>
        {`
          .${uniqueWrapperClass} .kfd-seg { transition: background .15s ease, color .15s ease, opacity .15s ease; }
          .${uniqueWrapperClass} .kfd-seg:hover:not(.kfd-seg--active) { background: ${contrastColor}0f; opacity: .9; }
          .${uniqueWrapperClass} .kfd-row { border-radius: 6px; transition: background .12s ease; }
          .${uniqueWrapperClass} .kfd-row:hover { background: ${contrastColor}0f; }
          .${uniqueWrapperClass} .kfd-link { background: transparent; border: none; padding: 0; cursor: pointer; font-size: 11px; font-weight: 600; letter-spacing: .2px; color: ${contrastColor}; opacity: .65; transition: opacity .12s ease; }
          .${uniqueWrapperClass} .kfd-link:hover { opacity: 1; text-decoration: underline; }
          .${uniqueWrapperClass} .kfd-scroll { scrollbar-width: thin; scrollbar-color: ${contrastColor}33 transparent; }
          .${uniqueWrapperClass} .kfd-scroll::-webkit-scrollbar { width: 8px; }
          .${uniqueWrapperClass} .kfd-scroll::-webkit-scrollbar-thumb { background: ${contrastColor}29; border-radius: 4px; }
          .${uniqueWrapperClass} .kfd-scroll::-webkit-scrollbar-thumb:hover { background: ${contrastColor}40; }
          .${uniqueWrapperClass} .kfd-scroll::-webkit-scrollbar-track { background: transparent; }
        `}
      </style>
      {hasTableBgColor && (
        <style>
          {`
            .${uniqueWrapperClass} .ant-checkbox-inner {
              background-color: transparent !important;
              border-color: ${contrastColor}99 !important;
            }
            .${uniqueWrapperClass} .ant-checkbox-checked .ant-checkbox-inner {
              background-color: ${contrastColor} !important;
              border-color: ${contrastColor} !important;
            }
            .${uniqueWrapperClass} .ant-checkbox-checked .ant-checkbox-inner::after {
              border-color: ${tableBgColor} !important;
            }
            .${uniqueWrapperClass} .ant-checkbox + span {
              color: ${contrastColor} !important;
            }
            .${uniqueWrapperClass} .ant-input {
              background-color: transparent !important;
              border-color: ${contrastColor} !important;
              color: ${contrastColor} !important;
            }
            .${uniqueWrapperClass} .ant-input::placeholder {
              color: ${contrastColor}80 !important;
            }
            .${uniqueWrapperClass} .ant-select {
              background-color: transparent !important;
            }
            .${uniqueWrapperClass} .ant-select-selector {
              background-color: transparent !important;
              border-color: ${contrastColor} !important;
              color: ${contrastColor} !important;
            }
            .${uniqueWrapperClass} .ant-select-selection-item {
              color: ${contrastColor} !important;
            }
            .${uniqueWrapperClass} .ant-select-arrow {
              color: ${contrastColor} !important;
            }
            .${uniqueWrapperClass} .ant-input-number {
              background-color: transparent !important;
              border-color: ${contrastColor} !important;
            }
            .${uniqueWrapperClass} .ant-input-number-input {
              background-color: transparent !important;
              color: ${contrastColor} !important;
            }
            .${uniqueWrapperClass} .ant-radio-wrapper span {
              color: ${contrastColor} !important;
            }
            .${uniqueWrapperClass} .ant-radio-inner {
              border-color: ${contrastColor} !important;
              background-color: transparent !important;
            }
            .${uniqueWrapperClass} .ant-radio-checked .ant-radio-inner {
              background-color: ${contrastColor} !important;
              border-color: ${contrastColor} !important;
            }
            .${uniqueWrapperClass} .ant-radio-checked .ant-radio-inner::after {
              background-color: ${tableBgColor} !important;
            }
            .${uniqueWrapperClass} .ant-btn {
              background-color: transparent !important;
              border-color: ${contrastColor} !important;
              color: ${contrastColor} !important;
            }
            .${uniqueWrapperClass} .ant-btn:hover, .${uniqueWrapperClass} .ant-btn:focus {
              background-color: ${contrastColor}20 !important;
              border-color: ${contrastColor} !important;
              color: ${contrastColor} !important;
            }
            .${uniqueWrapperClass} .ant-btn-primary {
              background-color: ${contrastColor} !important;
              border-color: ${contrastColor} !important;
              color: ${tableBgColor} !important;
            }
            .${uniqueWrapperClass} .ant-btn-primary:hover, .${uniqueWrapperClass} .ant-btn-primary:focus {
              background-color: ${contrastColor}CC !important;
              border-color: ${contrastColor}CC !important;
              color: ${tableBgColor} !important;
            }
            .${uniqueWrapperClass} .ant-divider {
              border-color: ${contrastColor}40 !important;
            }
          `}
        </style>
      )}

      <div
        style={{
          display: "flex",
          gap: 3,
          marginBottom: 12,
          padding: 3,
          borderRadius: 8,
          background: `${contrastColor}0f`
        }}
      >
        {renderTabButton("condition", isTr ? "Koşul" : "Condition")}
        {renderTabButton("value", isTr ? "Değerler" : "Values")}
        {hasColorFilter && renderTabButton("color", isTr ? "Renk" : "Color")}
      </div>

      {activeTab === "condition" && (
        <>
          <Select
            value={searchType}
            style={{ width: "100%", marginBottom: 8, display: "block" }}
            allowClear
            suffixIcon={<DropdownChevronIcon color="currentColor" />}
            onChange={(value) => {
              setSearchType(value);
              setSelectedKeys({
                value: searchValue,
                value2: searchValue2,
                type: value,
                columnId,
                logicalOperator,
                secondType: secondSearchType,
                secondValue: secondSearchValue,
                secondValue2: secondSearchValue2
              });
            }}
          >
            {isStringDataType && (
              <Option value={StringFilterTypes.Contains}>{language === "tr_TR" ? "İçerir" : "Contains"}</Option>
            )}
            {isStringDataType && (
              <Option value={StringFilterTypes.DoesNotContain}>
                {language === "tr_TR" ? "İçermez" : "Does Not Contain"}
              </Option>
            )}
            {isStringDataType && (
              <Option value={StringFilterTypes.Equals}>{language === "tr_TR" ? "Eşittir" : "Equals"}</Option>
            )}
            {isStringDataType && (
              <Option value={StringFilterTypes.DoesNotEqual}>
                {language === "tr_TR" ? "Eşit Değildir" : "Does Not Equal"}
              </Option>
            )}
            {isStringDataType && (
              <Option value={StringFilterTypes.BeginsWith}>{language === "tr_TR" ? "Başlar" : "Begins With"}</Option>
            )}
            {isStringDataType && (
              <Option value={StringFilterTypes.EndsWith}>{language === "tr_TR" ? "Biter" : "Ends With"}</Option>
            )}
            {isStringDataType && (
              <Option value={StringFilterTypes.Blank}>{language === "tr_TR" ? "Boş" : "Blank"}</Option>
            )}
            {isStringDataType && (
              <Option value={StringFilterTypes.NotBlank}>{language === "tr_TR" ? "Boş Değildir" : "Not Blank"}</Option>
            )}
            {dataType === ValueType.Number && (
              <Option value={NumberFilterTypes.Equals}>{language === "tr_TR" ? "Eşittir" : "Equals"}</Option>
            )}
            {dataType === ValueType.Number && (
              <Option value={NumberFilterTypes.DoesNotEqual}>
                {language === "tr_TR" ? "Eşit Değildir" : "Does Not Equal"}
              </Option>
            )}
            {dataType === ValueType.Number && (
              <Option value={NumberFilterTypes.GreaterThan}>
                {language === "tr_TR" ? "Büyüktür" : "Greater Than"}
              </Option>
            )}
            {dataType === ValueType.Number && (
              <Option value={NumberFilterTypes.GreaterThanOrEqual}>
                {language === "tr_TR" ? "Büyük Eşittir" : "Greater Than or Equal"}
              </Option>
            )}
            {dataType === ValueType.Number && (
              <Option value={NumberFilterTypes.LessThan}>{language === "tr_TR" ? "Küçüktür" : "Less Than"}</Option>
            )}
            {dataType === ValueType.Number && (
              <Option value={NumberFilterTypes.LessThanOrEqual}>
                {language === "tr_TR" ? "Küçük Eşittir" : "Less Than or Equal"}
              </Option>
            )}
            {dataType === ValueType.Number && (
              <Option value={NumberFilterTypes.Blank}>{language === "tr_TR" ? "Boş" : "Blank"}</Option>
            )}
            {dataType === ValueType.Number && (
              <Option value={NumberFilterTypes.NotBlank}>{language === "tr_TR" ? "Boş Değildir" : "Not Blank"}</Option>
            )}
            {dataType === ValueType.Date && (
              <Option value={DateFilterTypes.After}>{language === "tr_TR" ? "Tarihinden Sonra" : "After Date"}</Option>
            )}
            {dataType === ValueType.Date && (
              <Option value={DateFilterTypes.Before}>{language === "tr_TR" ? "Tarihinden Önce" : "Before Date"}</Option>
            )}
            {dataType === ValueType.Date && (
              <Option value={DateFilterTypes.Between}>
                {language === "tr_TR" ? "Tarihleri Arasında" : "Between Dates"}
              </Option>
            )}
            {dataType === ValueType.Date && (
              <Option value={DateFilterTypes.Equals}>{language === "tr_TR" ? "Eşittir" : "Equals"}</Option>
            )}
            {dataType === ValueType.Date && (
              <Option value={DateFilterTypes.DoesNotEqual}>
                {language === "tr_TR" ? "Eşit Değildir" : "Does Not Equal"}
              </Option>
            )}
            {dataType === ValueType.Date && (
              <Option value={DateFilterTypes.Blank}>{language === "tr_TR" ? "Boş" : "Blank"}</Option>
            )}
            {dataType === ValueType.Date && (
              <Option value={DateFilterTypes.NotBlank}>{language === "tr_TR" ? "Boş Değildir" : "Not Blank"}</Option>
            )}
            {dataType === ValueType.Boolean && (
              <Option value={BooleanFilterTypes.Equals}>{language === "tr_TR" ? "Eşittir" : "Equals"}</Option>
            )}
            {dataType === ValueType.Boolean && (
              <Option value={BooleanFilterTypes.DoesNotEqual}>
                {language === "tr_TR" ? "Eşit Değildir" : "Does Not Equal"}
              </Option>
            )}
            {dataType === ValueType.Boolean && (
              <Option value={BooleanFilterTypes.Blank}>{language === "tr_TR" ? "Boş" : "Blank"}</Option>
            )}
            {dataType === ValueType.Boolean && (
              <Option value={BooleanFilterTypes.NotBlank}>{language === "tr_TR" ? "Boş Değildir" : "Not Blank"}</Option>
            )}
          </Select>

          {isString(searchType) && !isBlank && !isNotBlank && (
            <Input
              placeholder={language === "tr_TR" ? "Filtrele..." : "Filter..."}
              value={searchValue}
              onChange={(e) => handleSearchValueChange(e)}
              style={{ width: "100%", marginBottom: 8, display: "block" }}
              autoFocus
              readOnly={isDesignTime}
              allowClear
            />
          )}
          {isNumber(searchType) && !isBlank && !isNotBlank && (
            <InputNumber
              placeholder={language === "tr_TR" ? "Filtrele..." : "Filter..."}
              value={searchValue}
              onChange={(e) => handleSearchValueChange(e)}
              style={{ width: "100%", marginBottom: 8, display: "block" }}
              autoFocus
              readOnly={isDesignTime}
            />
          )}
          {isDate(searchType) && !isBlank && !isNotBlank && (
            <div style={{ width: "100%", marginBottom: 8 }}>
              <KDatePicker
                format={dateSearchFormat}
                displayType={
                  (typeof searchValue === "string" && (searchValue.includes(":") || searchValue.includes("T"))) ||
                  (dateSearchFormat &&
                    (dateSearchFormat.includes("T") ||
                      dateSearchFormat.includes("HH:mm:ss") ||
                      dateSearchFormat.includes("HH:mm") ||
                      dateSearchFormat.includes("mm:ss")))
                    ? ("datetime" as any)
                    : ("date" as any)
                }
                placeholder={language === "tr_TR" ? "Filtrele..." : "Filter..."}
                value={searchValue}
                isTableSearch
                onChange={(val) => handleSearchValueChange(val)}
                disabled={isDesignTime}
                returnDisplayValue
              />
            </div>
          )}
          {searchType === NumberFilterTypes.Between && (
            <InputNumber
              placeholder={language === "tr_TR" ? "İkinci değer..." : "Second value..."}
              value={searchValue2}
              onChange={(e) => handleSearchValueChange(e, true)}
              style={{ width: "100%", marginBottom: 8, display: "block" }}
              readOnly={isDesignTime}
            />
          )}
          {searchType === DateFilterTypes.Between && (
            <div style={{ width: "100%", marginBottom: 8 }}>
              <KDatePicker
                format={dateSearchFormat}
                displayType={
                  (typeof searchValue2 === "string" && (searchValue2.includes(":") || searchValue2.includes("T"))) ||
                  (dateSearchFormat &&
                    (dateSearchFormat.includes("T") ||
                      dateSearchFormat.includes("HH:mm:ss") ||
                      dateSearchFormat.includes("HH:mm") ||
                      dateSearchFormat.includes("mm:ss")))
                    ? ("datetime" as any)
                    : ("date" as any)
                }
                placeholder={language === "tr_TR" ? "Filtrele..." : "Filter..."}
                value={searchValue2}
                isTableSearch
                onChange={(val) => handleSearchValueChange(val, true)}
                returnDisplayValue
              />
            </div>
          )}
          {isBoolean(searchType) && !isBlank && !isNotBlank && (
            <TriStateSwitch
              value={searchValue || null}
              onChange={(value) => {
                setSearchValue(value);
                setSelectedKeys({
                  value,
                  type: searchType,
                  columnId,
                  logicalOperator,
                  secondType: secondSearchType,
                  secondValue: secondSearchValue,
                  secondValue2: secondSearchValue2
                });
              }}
              language={language}
              source={"FilterDropdown"}
            />
          )}

          <Radio.Group
            value={logicalOperator}
            onChange={handleLogicalOperatorChange}
            style={{ width: "100%", marginBottom: 8, display: "flex", justifyContent: "center" }}
          >
            <Radio value="AND" style={{ display: "flex", alignItems: "center" }}>
              <span style={{ marginLeft: 8 }}>AND</span>
            </Radio>
            <Radio value="OR" style={{ display: "flex", alignItems: "center" }}>
              <span style={{ marginLeft: 8 }}>OR</span>
            </Radio>
          </Radio.Group>

          {showSecondFilter && (
            <>
              <Divider style={{ margin: "8px 0" }} />

              <Select
                value={secondSearchType}
                style={{ width: "100%", marginBottom: 8, display: "block" }}
                allowClear
                suffixIcon={<DropdownChevronIcon color="currentColor" />}
                onChange={(value) => {
                  setSecondSearchType(value);
                  setSelectedKeys({
                    value: searchValue,
                    value2: searchValue2,
                    type: searchType,
                    columnId,
                    logicalOperator,
                    secondType: value,
                    secondValue: secondSearchValue,
                    secondValue2: secondSearchValue2
                  });
                }}
              >
                {isStringDataType && (
                  <Option value={StringFilterTypes.Contains}>{language === "tr_TR" ? "İçerir" : "Contains"}</Option>
                )}
                {isStringDataType && (
                  <Option value={StringFilterTypes.DoesNotContain}>
                    {language === "tr_TR" ? "İçermez" : "Does Not Contain"}
                  </Option>
                )}
                {isStringDataType && (
                  <Option value={StringFilterTypes.Equals}>{language === "tr_TR" ? "Eşittir" : "Equals"}</Option>
                )}
                {isStringDataType && (
                  <Option value={StringFilterTypes.DoesNotEqual}>
                    {language === "tr_TR" ? "Eşit Değildir" : "Does Not Equal"}
                  </Option>
                )}
                {isStringDataType && (
                  <Option value={StringFilterTypes.BeginsWith}>
                    {language === "tr_TR" ? "Başlar" : "Begins With"}
                  </Option>
                )}
                {isStringDataType && (
                  <Option value={StringFilterTypes.EndsWith}>{language === "tr_TR" ? "Biter" : "Ends With"}</Option>
                )}
                {isStringDataType && (
                  <Option value={StringFilterTypes.Blank}>{language === "tr_TR" ? "Boş" : "Blank"}</Option>
                )}
                {isStringDataType && (
                  <Option value={StringFilterTypes.NotBlank}>
                    {language === "tr_TR" ? "Boş Değildir" : "Not Blank"}
                  </Option>
                )}
                {dataType === ValueType.Number && (
                  <Option value={NumberFilterTypes.Equals}>{language === "tr_TR" ? "Eşittir" : "Equals"}</Option>
                )}
                {dataType === ValueType.Number && (
                  <Option value={NumberFilterTypes.DoesNotEqual}>
                    {language === "tr_TR" ? "Eşit Değildir" : "Does Not Equal"}
                  </Option>
                )}
                {dataType === ValueType.Number && (
                  <Option value={NumberFilterTypes.GreaterThan}>
                    {language === "tr_TR" ? "Büyüktür" : "Greater Than"}
                  </Option>
                )}
                {dataType === ValueType.Number && (
                  <Option value={NumberFilterTypes.GreaterThanOrEqual}>
                    {language === "tr_TR" ? "Büyük Eşittir" : "Greater Than or Equal"}
                  </Option>
                )}
                {dataType === ValueType.Number && (
                  <Option value={NumberFilterTypes.LessThan}>{language === "tr_TR" ? "Küçüktür" : "Less Than"}</Option>
                )}
                {dataType === ValueType.Number && (
                  <Option value={NumberFilterTypes.LessThanOrEqual}>
                    {language === "tr_TR" ? "Küçük Eşittir" : "Less Than or Equal"}
                  </Option>
                )}
                {dataType === ValueType.Number && (
                  <Option value={NumberFilterTypes.Blank}>{language === "tr_TR" ? "Boş" : "Blank"}</Option>
                )}
                {dataType === ValueType.Number && (
                  <Option value={NumberFilterTypes.NotBlank}>
                    {language === "tr_TR" ? "Boş Değildir" : "Not Blank"}
                  </Option>
                )}
                {dataType === ValueType.Date && (
                  <Option value={DateFilterTypes.After}>
                    {language === "tr_TR" ? "Tarihinden Sonra" : "After Date"}
                  </Option>
                )}
                {dataType === ValueType.Date && (
                  <Option value={DateFilterTypes.Before}>
                    {language === "tr_TR" ? "Tarihinden Önce" : "Before Date"}
                  </Option>
                )}
                {dataType === ValueType.Date && (
                  <Option value={DateFilterTypes.Between}>
                    {language === "tr_TR" ? "Tarihleri Arasında" : "Between Dates"}
                  </Option>
                )}
                {dataType === ValueType.Date && (
                  <Option value={DateFilterTypes.Equals}>{language === "tr_TR" ? "Eşittir" : "Equals"}</Option>
                )}
                {dataType === ValueType.Date && (
                  <Option value={DateFilterTypes.DoesNotEqual}>
                    {language === "tr_TR" ? "Eşit Değildir" : "Does Not Equal"}
                  </Option>
                )}
                {dataType === ValueType.Date && (
                  <Option value={DateFilterTypes.Blank}>{language === "tr_TR" ? "Boş" : "Blank"}</Option>
                )}
                {dataType === ValueType.Date && (
                  <Option value={DateFilterTypes.NotBlank}>
                    {language === "tr_TR" ? "Boş Değildir" : "Not Blank"}
                  </Option>
                )}
                {dataType === ValueType.Boolean && (
                  <Option value={BooleanFilterTypes.Equals}>{language === "tr_TR" ? "Eşittir" : "Equals"}</Option>
                )}
                {dataType === ValueType.Boolean && (
                  <Option value={BooleanFilterTypes.DoesNotEqual}>
                    {language === "tr_TR" ? "Eşit Değildir" : "Does Not Equal"}
                  </Option>
                )}
                {dataType === ValueType.Boolean && (
                  <Option value={BooleanFilterTypes.Blank}>{language === "tr_TR" ? "Boş" : "Blank"}</Option>
                )}
                {dataType === ValueType.Boolean && (
                  <Option value={BooleanFilterTypes.NotBlank}>
                    {language === "tr_TR" ? "Boş Değildir" : "Not Blank"}
                  </Option>
                )}
              </Select>

              {isString(secondSearchType) && !isSecondBlank && !isSecondNotBlank && (
                <Input
                  placeholder={language === "tr_TR" ? "Filtrele..." : "Filter..."}
                  value={secondSearchValue}
                  onChange={(e) => handleSecondSearchValueChange(e)}
                  style={{ width: "100%", marginBottom: 8, display: "block" }}
                  readOnly={isDesignTime}
                  allowClear
                />
              )}
              {isNumber(secondSearchType) && !isSecondBlank && !isSecondNotBlank && (
                <InputNumber
                  placeholder={language === "tr_TR" ? "Filtrele..." : "Filter..."}
                  value={secondSearchValue}
                  onChange={(e) => handleSecondSearchValueChange(e)}
                  style={{ width: "100%", marginBottom: 8, display: "block" }}
                  readOnly={isDesignTime}
                />
              )}
              {secondSearchType === NumberFilterTypes.Between && (
                <InputNumber
                  placeholder={language === "tr_TR" ? "İkinci değer..." : "Second value..."}
                  value={secondSearchValue2}
                  onChange={(e) => handleSecondSearchValueChange(e, true)}
                  style={{ width: "100%", marginBottom: 8, display: "block" }}
                  readOnly={isDesignTime}
                />
              )}
              {isDate(secondSearchType) && !isSecondBlank && !isSecondNotBlank && (
                <div style={{ width: "100%", marginBottom: 8 }}>
                  <KDatePicker
                    format={dateSearchFormat}
                    displayType={
                      (typeof secondSearchValue === "string" &&
                        (secondSearchValue.includes(":") || secondSearchValue.includes("T"))) ||
                      (dateSearchFormat &&
                        (dateSearchFormat.includes("T") ||
                          dateSearchFormat.includes("HH:mm:ss") ||
                          dateSearchFormat.includes("HH:mm") ||
                          dateSearchFormat.includes("mm:ss")))
                        ? ("datetime" as any)
                        : ("date" as any)
                    }
                    placeholder={language === "tr_TR" ? "Filtrele..." : "Filter..."}
                    value={secondSearchValue}
                    isTableSearch
                    onChange={(val) => handleSecondSearchValueChange(val)}
                    disabled={isDesignTime}
                    returnDisplayValue
                  />
                </div>
              )}
              {secondSearchType === DateFilterTypes.Between && (
                <div style={{ width: "100%", marginBottom: 8 }}>
                  <KDatePicker
                    format={dateSearchFormat}
                    displayType={
                      (typeof secondSearchValue2 === "string" &&
                        (secondSearchValue2.includes(":") || secondSearchValue2.includes("T"))) ||
                      (dateSearchFormat &&
                        (dateSearchFormat.includes("T") ||
                          dateSearchFormat.includes("HH:mm:ss") ||
                          dateSearchFormat.includes("HH:mm") ||
                          dateSearchFormat.includes("mm:ss")))
                        ? ("datetime" as any)
                        : ("date" as any)
                    }
                    placeholder={language === "tr_TR" ? "İkinci değer..." : "Second value..."}
                    value={secondSearchValue2}
                    isTableSearch
                    onChange={(val) => handleSecondSearchValueChange(val, true)}
                    returnDisplayValue
                  />
                </div>
              )}
              {isBoolean(secondSearchType) && !isSecondBlank && !isSecondNotBlank && (
                <TriStateSwitch
                  value={secondSearchValue || null}
                  onChange={(value) => {
                    setSecondSearchValue(value);
                    setSelectedKeys({
                      value: searchValue,
                      value2: searchValue2,
                      type: searchType,
                      columnId,
                      logicalOperator,
                      secondType: secondSearchType,
                      secondValue: value,
                      secondValue2: secondSearchValue2
                    });
                  }}
                  language={language}
                  source={"FilterDropdown"}
                />
              )}
            </>
          )}
        </>
      )}

      {activeTab === "value" && (
        <div style={{ marginBottom: 4 }}>
          <Input
            placeholder={isTr ? "Değerlerde ara..." : "Search values..."}
            value={valueSearch}
            onChange={(e) => setValueSearch(e.target.value)}
            style={{ width: "100%", marginBottom: 10 }}
            prefix={
              <svg
                width={13}
                height={13}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ opacity: 0.5 }}
              >
                <circle cx={11} cy={11} r={7} />
                <path d="M21 21l-4.3-4.3" />
              </svg>
            }
            allowClear
          />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 6,
              padding: "0 2px"
            }}
          >
            <span style={{ fontSize: 11, color: contrastColor, opacity: 0.55 }}>
              {selectedValues.size > 0
                ? `${selectedValues.size} ${isTr ? "seçili" : "selected"}`
                : isTr
                  ? "Tümü"
                  : "All"}
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <button type="button" className="kfd-link" onClick={selectAllVisibleValues}>
                {isTr ? "Tümünü Seç" : "Select All"}
              </button>
              <span style={{ opacity: 0.3, color: contrastColor }}>·</span>
              <button type="button" className="kfd-link" onClick={clearAllValues}>
                {isTr ? "Temizle" : "Clear All"}
              </button>
            </span>
          </div>
          <div className="kfd-scroll" style={{ maxHeight: 208, overflowY: "auto", margin: "0 -2px" }}>
            {visibleValues.length === 0 && (
              <div style={{ padding: "16px 8px", textAlign: "center", opacity: 0.5, fontSize: 12 }}>
                {isTr ? "Eşleşen değer yok" : "No matching values"}
              </div>
            )}
            {visibleValues.map((item) => {
              const isBlankEntry: boolean = item.key === BLANK_FILTER_KEY;
              return (
                <div key={item.key} className="kfd-row" style={{ padding: "5px 8px" }}>
                  <Checkbox
                    checked={selectedValues.has(item.key)}
                    onChange={(e) => toggleSelectedValue(item.key, e.target.checked)}
                    style={{ width: "100%", color: contrastColor }}
                  >
                    <span
                      style={{
                        fontSize: 13,
                        fontStyle: isBlankEntry ? "italic" : "normal",
                        opacity: isBlankEntry ? 0.6 : 1
                      }}
                    >
                      {item.label}
                    </span>
                  </Checkbox>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {activeTab === "color" && hasColorFilter && (
        <div style={{ marginBottom: 4 }}>
          <div style={{ fontSize: 11, color: contrastColor, opacity: 0.55, marginBottom: 6, padding: "0 2px" }}>
            {isTr ? "Hücre arka plan rengine göre filtrele" : "Filter by cell background color"}
          </div>
          <div className="kfd-scroll" style={{ maxHeight: 208, overflowY: "auto", margin: "0 -2px" }}>
            {uniqueColors.length === 0 && (
              <div style={{ padding: "16px 8px", textAlign: "center", opacity: 0.5, fontSize: 12 }}>
                {isTr ? "Renklendirilmiş hücre yok" : "No colored cells"}
              </div>
            )}
            {uniqueColors.map((color) => (
              <div key={color} className="kfd-row" style={{ padding: "5px 8px" }}>
                <Checkbox
                  checked={selectedColors.has(color)}
                  onChange={(e) => toggleSelectedColor(color, e.target.checked)}
                  style={{ width: "100%", color: contrastColor }}
                >
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 9 }}>
                    <span
                      data-testid="color-swatch"
                      style={{
                        display: "inline-block",
                        width: 18,
                        height: 18,
                        borderRadius: 5,
                        background: color,
                        border: `1px solid ${contrastColor}26`,
                        boxShadow: `inset 0 0 0 1px ${surfaceColor}, 0 1px 2px ${contrastColor}1f`,
                        flexShrink: 0
                      }}
                    />
                    <span
                      style={{
                        fontSize: 12,
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                        opacity: 0.7,
                        letterSpacing: -0.2
                      }}
                    >
                      {color}
                    </span>
                  </span>
                </Checkbox>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ height: 1, background: `${contrastColor}14`, margin: "10px -10px 0" }} />
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <Button type="primary" onClick={handleSearch} size="small" style={{ flex: 1 }}>
          {language === "tr_TR" ? "Filtrele" : "Filter"}
        </Button>
        <Button onClick={handleReset} size="small" style={{ flex: 1 }}>
          {language === "tr_TR" ? "Sıfırla" : "Reset"}
        </Button>
      </div>
    </div>
  );
};

export default CustomFilterDropdown;
